/**
 * PreviewMetricsCard — visual card aggregating header + body.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, memo, useRef } from 'react';

import PathCapUsageSection from '@/features/flow-optimizer/components/cap-usage/PathCapUsageSection';

import { PreviewMetricsBody, PreviewMetricsHeader } from './sections';
import { useScrollLockFallback } from './use-scroll-lock-fallback';
import type { PreviewRow } from '../preview-row.types';

export interface PreviewMetricsCardProps {
  name: string;
  row: PreviewRow;
}

// Native scrolling + `overscroll-contain` keeps the wheel inside the popup (and
// off the drawer behind) without a manual handler — manual scrollTop fought the
// browser's own scroll and killed the momentum/fluidity. The exception is the
// optimizer drawer, where Radix's scroll lock cancels the wheel out here;
// useScrollLockFallback re-drives the scroll ONLY once something has already
// cancelled it, so the fluid native path is untouched everywhere else.
const PreviewMetricsCard: FC<PreviewMetricsCardProps> = ({ name, row }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollLockFallback(scrollRef);
  return (
    <div
      ref={scrollRef}
      role="dialog"
      aria-label={`Preview metrics for ${name || row.itemName}`}
      className="max-h-[min(32rem,calc(100vh-24px))] w-[22rem] overflow-y-auto overscroll-contain rounded-lg border border-border/70 bg-popover text-popover-foreground shadow-xl ring-1 ring-black/5"
    >
      <PreviewMetricsHeader name={name} row={row} />
      <PathCapUsageSection row={row} />
      <PreviewMetricsBody row={row} />
    </div>
  );
};

// Memoised so repositioning the card on scroll (parent state change) doesn't
// re-render this heavy body — only the wrapper's top/left style updates.
export default memo(PreviewMetricsCard);
